import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Helmet } from "react-helmet";

import { assetService } from "../services/assetService";
import { systemService } from "../services/systemService";
import { partnerService, Partner } from "../services/partnerService";
import { WellSuperApp } from "../services/partnerWellSuperAppService";

import { normalizeAgreementLinks } from "../helpers/normalizeAgreementLinks";
import { getAppBasePath } from "../helpers/getAppBasePath";

interface Agreement {
  AgreementHeaderEn: string;
  AgreementHeaderTh: string;
  AgreementHtmlEn: string;
  AgreementHtmlTh: string;
}

const Privacy: React.FC = () => {
  const navigate = useNavigate();

  const [logo, setLogo] = useState<string | undefined>();
  const [system] = useState(systemService.getSystem(window.location));
  const [partner, setPartner] = useState<Partner | undefined>();
  const [agreement, setAgreement] = useState<Agreement | undefined>();
  const [isShowAgreement, setIsShowAgreement] = useState(false);

  const appBasePath = getAppBasePath();
  const privacyLinkPath = `${appBasePath}/privacy`;

  useEffect(() => {
    const currentPartner = partnerService.getPartner();

    if (!currentPartner) {
      navigate("/"); // redirect if no partner
      return;
    }

    setPartner(currentPartner);
    setLogo(assetService.getLogo(currentPartner.name));

    const loadAgreement = async () => {
      const hospitalIdStr = new URLSearchParams(window.location.search).get("hospitalId");
      const hospitalId = hospitalIdStr && !isNaN(parseInt(hospitalIdStr, 10))
        ? parseInt(hospitalIdStr, 10)
        : undefined;
      const hospitalName = hospitalId ? undefined : (currentPartner as WellSuperApp)?.hospitalName;
      const agreementData = await systemService.getAgreement(
        "PrivacyNotice",
        currentPartner.name,
        hospitalName,
        hospitalId
      );
      if (agreementData) {
        setAgreement(agreementData);
        setIsShowAgreement(true);
      }
    };

    loadAgreement();
  }, [navigate]); // run once on mount

  return (
    <div className="flex flex-col items-center p-0.5 md:p-10 gap-y-4 justify-center min-h-screen w-full bg-white">
      <Helmet>
        <title>Privacy Notice</title>
        <meta name="description" content="Privacy notice page" />
      </Helmet>

      <div className="main-container p-6">
        {/* Logo */}
        {logo && (
          <div className="flex justify-center items-center mb-8 logo-container">
            <img
              src={logo}
              alt="Logo"
              className="logo-image w-[172px] h-[64px] min-w-[172px] object-contain"
            />
          </div>
        )}

        {/* Agreement Header */}
        {isShowAgreement && agreement && (
          <div className="header mb-4 text-center">
            <p
              className="text-lg font-semibold"
              dangerouslySetInnerHTML={{
                __html:
                  system.language === "en"
                    ? agreement.AgreementHeaderEn
                    : agreement.AgreementHeaderTh,
              }}
            />
          </div>
        )}

        {/* Agreement Content */}
        {isShowAgreement && agreement && (
          <div className="prose max-w-full">
            <div
              dangerouslySetInnerHTML={{
                __html:
                  system.language === "en"
                    ? normalizeAgreementLinks(
                        agreement.AgreementHtmlEn ?? "",
                        privacyLinkPath
                      )
                    : normalizeAgreementLinks(
                        agreement.AgreementHtmlTh ?? "",
                        privacyLinkPath
                      ),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Privacy;
